import React, { useRef, useState } from "react";
import { staffApi, formatApiError, BACKEND_URL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ImagePlus, X } from "lucide-react";

// Multi-photo picker for G.O.A.L records. Uploads full-frame (no crop) to
// object storage via POST /uploads/photo and keeps `[{id, url, filename}]`.
export const GoalPhotoUpload = ({ photos = [], onChange, disabled = false }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    const added = [];
    for (const f of files) {
      if (!f.type.startsWith("image/")) { toast.error(`${f.name} is not an image`); continue; }
      try {
        const fd = new FormData();
        fd.append("file", f);
        const r = await staffApi.post("/uploads/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
        added.push({ id: r.data.id, url: r.data.url, filename: r.data.filename || f.name });
      } catch (err) { toast.error(formatApiError(err)); }
    }
    if (added.length) onChange?.([...photos, ...added]);
    setBusy(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const src = (u) => (u?.startsWith("http") ? u : `${BACKEND_URL}${u}`);

  return (
    <div data-testid="goal-photo-upload">
      <div className="flex flex-wrap gap-2">
        {photos.map((p) => (
          <div key={p.id} className="relative w-20 h-20 rounded-md overflow-hidden border border-border group">
            <img src={src(p.url)} alt={p.filename || "photo"} className="w-full h-full object-cover" data-testid={`goal-photo-${p.id}`} />
            {!disabled && (
              <button
                type="button"
                onClick={() => onChange?.(photos.filter((x) => x.id !== p.id))}
                className="absolute top-0.5 right-0.5 bg-black/60 text-white rounded-full p-0.5"
                data-testid={`goal-photo-remove-${p.id}`}
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
        ))}
        {!disabled && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={busy}
            className="w-20 h-20 rounded-md border border-dashed border-border flex flex-col items-center justify-center gap-1 text-muted-foreground hover:bg-muted/50 transition-colors"
            data-testid="goal-photo-add-btn"
          >
            <ImagePlus className="w-5 h-5" />
            <span className="text-[10px]">{busy ? "Uploading…" : "Add"}</span>
          </button>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={onFiles}
        data-testid="goal-photo-input"
      />
    </div>
  );
};

export default GoalPhotoUpload;
